import Debounce from '@sd-utility/debounce';
import PropTypes from 'prop-types';
import React from 'react';
import { logos } from '../../fuse-brand-logos/src/brand-logos';
import imageAspectRatios from './config';
import {
	StyledBrandLogos,
	StyledContentBody,
	StyledContentCTA,
	StyledContentPattern,
	StyledContentWrapper,
	StyledIconWrapper,
	StyledImageWrapper,
	StyledPicture,
	StyledResizeStackedCTA,
	StyledVideoWrapper,
} from './styled-content-pattern';

const { forwardRef, useEffect, useRef, useState } = React;

const displayName = 'ContentPattern';

const propTypes = {
	brandLogo: PropTypes.shape({
		alt: PropTypes.string,
		brand: PropTypes.oneOf(Object.keys(logos)),
	}),
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	className: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	dataTestId: PropTypes.string,
	icon: PropTypes.element,
	imageWrapper: PropTypes.shape({
		imageAlignment: PropTypes.oneOf(['center', 'top']),
		imagePosition: PropTypes.oneOf(['left', 'right']),
		imageRatio: PropTypes.oneOf(imageAspectRatios), // ['1:1', '16:9', '4:3', '5:4', '3:4']
	}),
	picture: PropTypes.shape({
		alt: PropTypes.string,
		fallbackSource: PropTypes.string,
		fetchPriority: PropTypes.oneOf(['auto', 'low', 'high']),
		isLazy: PropTypes.bool,
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string,
				type: PropTypes.string,
			}),
		),
	}),
	videoPlayerSlot: PropTypes.node,
};

const defaultProps = {
	brandLogo: undefined,
	className: '',
	dataAnalyticsId: '',
	dataTestId: '',
	icon: undefined,
	imageWrapper: {
		imageAlignment: 'center',
		imagePosition: 'left',
		imageRatio: '1:1',
	},
	picture: {
		alt: '',
		fallbackSource: '',
		fetchPriority: 'auto',
		isLazy: false,
		position: 'left',
		sources: [{ srcSet: '', type: '' }],
	},
	videoPlayerSlot: undefined,
};

const ContentPattern = forwardRef(
	(
		{
			brandLogo,
			children,
			className,
			dataAnalyticsId,
			dataTestId,
			icon,
			imageWrapper,
			picture,
			videoPlayerSlot,
			...others
		},
		ref,
	) => {
		// Refs
		const contentWrapperRef = useRef();
		const ctaContainerRef = useRef();
		const contentBodyRef = useRef();
		const [copyWrapperWidth, setCopyWrapperWidth] = useState(0);
		// Desctructure object
		const { imageAlignment, imagePosition, imageRatio } = imageWrapper;
		const { fallbackSource, ...restPicture } = picture;
		const isPicture = !!fallbackSource;
		// Array of children passed either as cta or default content body
		const contentBody = [];
		const contentCTA = [];
		const contentButton = [];

		// Sort children into categories of either contentBody, contentCTA or contentButton
		React.Children.forEach(children, (child) => {
			if (
				React.isValidElement(child) &&
				child.props.slot === 'cta' &&
				child.props.type === 'button'
			) {
				contentButton.push(child);
			} else if (
				React.isValidElement(child) &&
				child.props.slot === 'cta' &&
				child.props.type !== 'button'
			) {
				contentCTA.push(child);
			} else {
				contentBody.push(child);
			}
		});

		useEffect(() => {
			setCopyWrapperWidth(contentBodyRef.current.offsetWidth);

			const handleResize = Debounce(() => {
				setCopyWrapperWidth(contentBodyRef.current.offsetWidth);
			}, 200);

			window.addEventListener('resize', handleResize);

			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}, []);

		return (
			<StyledContentPattern
				{...others}
				className={className}
				data-analyticsid={dataAnalyticsId}
				data-testid={dataTestId}
				imageAlignment={imageAlignment}
				imagePosition={imagePosition}
				ref={ref}
			>
				{videoPlayerSlot ? (
					<StyledVideoWrapper>{videoPlayerSlot}</StyledVideoWrapper>
				) : (
					<StyledImageWrapper imageRatio={imageRatio} isPicture={isPicture}>
						{picture && picture.fallbackSource && (
							<StyledPicture
								imageRatio={imageRatio}
								fallbackSource={fallbackSource}
								{...restPicture}
							/>
						)}
						{icon && !picture?.fallbackSource && !brandLogo && (
							<StyledIconWrapper>{icon}</StyledIconWrapper>
						)}
						{brandLogo && !picture?.fallbackSource && !icon && <StyledBrandLogos {...brandLogo} />}
					</StyledImageWrapper>
				)}
				<StyledContentWrapper ref={contentWrapperRef}>
					<StyledContentBody ref={contentBodyRef}>{contentBody}</StyledContentBody>
					{contentCTA.length > 0 && (
						<StyledContentCTA ref={ctaContainerRef}>{contentCTA}</StyledContentCTA>
					)}
					{contentButton.length === 2 && (
						<StyledResizeStackedCTA parentContentWidth={copyWrapperWidth}>
							{contentButton}
						</StyledResizeStackedCTA>
					)}
				</StyledContentWrapper>
			</StyledContentPattern>
		);
	},
);

ContentPattern.defaultProps = defaultProps;
ContentPattern.displayName = displayName;
ContentPattern.propTypes = propTypes;

export { ContentPattern };

export default ContentPattern;
